/** Dashboard summary from GET /api/v1/dashboard/summary */
export interface DashboardSummary {
  open_alerts: SeverityCounts;
  open_incidents: number;
  recent_alerts_24h: number;
  threat_level: "critical" | "high" | "medium" | "low";
}

export interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/** Single trend data point from GET /api/v1/dashboard/trends */
export interface TrendDataPoint {
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/** Dashboard trends response */
export interface DashboardTrends {
  trends: TrendDataPoint[];
  days: number;
}

export type AlertSeverity = "critical" | "high" | "medium" | "low";
export type AlertStatus = "new" | "acknowledged" | "investigating" | "resolved";
export type EnrichmentStatus = "pending" | "complete" | "failed";

export interface AiContext {
  summary: string;
  severity_reasoning: string;
  recommended_actions: string[];
  mitre_technique: string | null;
  token_usage?: { input_tokens: number; output_tokens: number };
}

export interface AlertNote {
  user_id: string;
  author_email?: string | null;
  text: string;
  created_at: string;
}

/** Alert from GET /api/v1/alerts and GET /api/v1/alerts/:id */
export interface Alert {
  id: string;
  tenant_id: string;
  rule_id: string | null;
  incident_id: string | null;
  title: string;
  description: string | null;
  severity: AlertSeverity;
  status: AlertStatus;
  source_type: string;
  matched_events: Record<string, unknown>[];
  entities: Record<string, string[]>;
  context: Record<string, unknown>;
  ai_context: AiContext | null;
  enrichment_status: EnrichmentStatus | null;
  dedup_key: string;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  feedback: "true_positive" | "false_positive" | null;
  feedback_note: string | null;
  created_at: string;
  updated_at: string;
  assigned_to?: string | null;
  notes?: AlertNote[];
  /** #665: true if a notification for this alert was suppressed by an active mute */
  notification_suppressed?: boolean;
}

/** Alerts list response */
export interface AlertOwnerCounts {
  all: number;
  mine: number;
  unassigned: number;
}

export interface AlertsResponse {
  alerts: Alert[];
  counts: SeverityCounts;
  owner_counts: AlertOwnerCounts;
  total: number;
  limit: number;
  offset: number;
}

/** Integration from GET /api/v1/integrations */
export interface Integration {
  id: string;
  tenant_id: string;
  source_type: IntegrationSourceType;
  display_name: string;
  config: Record<string, unknown>;
  credentials_secret_arn: string | null;
  health_status: "unknown" | "healthy" | "unhealthy" | "disabled";
  last_poll_status: string | null;
  last_poll_event_count: number | null;
  last_error_message: string | null;
  created_at: string;
  updated_at: string;
  user_enabled: boolean;
  /** ISO-8601 timestamp of the last successfully ingested event batch, or null if never. */
  last_event_at: string | null;
  /** Recent endpoint hosts backing the Endpoint Agent integration row. */
  endpoint_agents: EndpointIntegrationAgent[];
}

export interface EndpointIntegrationAgent {
  id: string;
  hostname: string | null;
  os_type: "linux" | "windows" | "macos" | string;
  status: string;
  last_heartbeat_at: string | null;
  health_metrics: HealthMetrics | null;
  health_metrics_at: string | null;
}

export type IntegrationSourceType =
  | "entra_id"
  | "crowdstrike"
  | "sentinel"
  | "m365_defender"
  | "cloudtrail"
  | "azure_activity"
  | "gcp_audit"
  | "google_workspace"
  | "m365"
  | "mimecast_email"
  | "endpoint"
  | "firewall";

export interface IntegrationsResponse {
  integrations: Integration[];
}

export interface IntegrationTestResult {
  status: "success" | "warning";
  integration_id: string;
  source_type: string;
  checks: { name: string; passed: boolean; detail: string }[];
}

export interface CreateIntegrationRequest {
  source_type: IntegrationSourceType;
  display_name: string;
  config: Record<string, unknown>;
  credentials_secret_arn?: string;
}

export interface CreateIntegrationResponse {
  integration: Integration;
  /** False when endpoint setup reused the tenant's existing logical source. */
  created: boolean;
}

/** Cognito token response */
export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

/** Decoded JWT claims (minimal subset we need) */
export interface JwtClaims {
  sub: string;
  email?: string;
  "cognito:username"?: string;
  "cognito:groups"?: string[];
  "custom:tenant_id"?: string;
  "custom:is_superadmin"?: string;
  "custom:role"?: string;
  "custom:is_msp"?: string;
  exp: number;
  iat: number;
}

/** User derived from token claims */
export interface User {
  sub: string;
  email: string;
  username: string;
  tenantId: string;
  roles: string[];
  isSuperadmin: boolean;
}

// ── Syslog / Firewall Sources ──────────────────────────────

export type SyslogVendor =
  | "paloalto"
  | "fortinet"
  | "sonicwall"
  | "cisco"
  | "sophos";

export interface SyslogSource {
  id: string;
  name: string;
  vendor_type: SyslogVendor;
  source_ips: string[] | null;
  is_active: boolean;
  created_at: string;
  last_event_at: string | null;
  /** Where the relay POSTs events. Empty when the environment has no syslog API wired. */
  ingestion_url: string;
}

/**
 * The create response carries the key, which is never shown again. It has no
 * `last_event_at` — that column is absent from the insert's RETURNING clause and
 * a brand-new source has no events anyway.
 */
export interface SyslogSourceCreated extends Omit<SyslogSource, "last_event_at"> {
  key_prefix: string;
  raw_key: string;
}

export interface SyslogSourcesResponse {
  sources: SyslogSource[];
}

export interface CreateSyslogSourceRequest {
  name: string;
  vendor_type: SyslogVendor;
  source_ips?: string[];
}

// ── MSP Types ──────────────────────────────────────────────

export interface MspTenant {
  id: string;
  parent_msp_id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  created_at: string;
}

export interface MspTenantsResponse {
  tenants: MspTenant[];
  limit: number;
  offset: number;
}

export interface MspAlertSummary {
  tenant_id: string;
  tenant_name: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface MspDashboardSummaryResponse {
  summary: MspAlertSummary[];
  hours: number;
}

export interface MspIntegrationHealth {
  tenant_id: string;
  tenant_name: string;
  integration_id: string;
  source_type: string;
  display_name: string;
  health_status: "unknown" | "healthy" | "unhealthy" | "disabled";
  last_poll_at: string | null;
  consecutive_failures: number;
}

export interface MspDashboardHealthResponse {
  integrations: MspIntegrationHealth[];
}

export interface MspBillingChild {
  tenant_id: string;
  name: string;
  slug: string;
  endpoints: number;
  events: number;
  alerts: number;
  storage_bytes: number;
}

export interface MspBillingTotals {
  total_children: number;
  total_endpoints: number;
  total_events: number;
  total_alerts: number;
  total_storage_bytes: number;
  data_period: string;
  period_start: string;
}

export interface MspBillingResponse {
  billing_summary: MspBillingTotals;
  partner_tier: { partner_tier: string } | null;
  /** Present only when the request asked for detail=true. */
  children?: MspBillingChild[];
}

export interface MspCreateTenantRequest {
  name: string;
  slug: string;
  admin_email: string;
  plan?: string;
  template_id?: string;
}

// #771: matches the backend `safe_result` shape. temp_password is delivered to
// the admin by welcome email and raw_key never leaves the backend, so neither is
// returned here — only the non-secret key_prefix.
export interface MspCreateTenantResponse {
  tenant: { id: string; name: string; slug: string; plan: string };
  admin_user: { email: string };
  agent_key: { key_prefix: string };
  integrations_url: string;
}

// #782: bulk client onboarding. CSV is parsed client-side into this JSON array;
// the endpoint creates bare tenants (no admin user — that goes via the invite
// flow) and reports per-row outcomes so partial success is visible.
export interface MspBulkCreateClient {
  name: string;
  slug?: string;
  plan?: string;
  template_id?: string;
}

export interface MspBulkCreateResult {
  index: number;
  name: string;
  slug: string | null;
  status: "created" | "failed";
  tenant_id: string | null;
  error: string | null;
  warning: string | null;
}

export interface MspBulkCreateResponse {
  created_count: number;
  failed_count: number;
  results: MspBulkCreateResult[];
}

export interface MspOnboardingSummaryEntry {
  tenant_id: string;
  tenant_name: string;
  progress: string;
  completed: number;
  total: number;
}

export interface MspOnboardingSummaryResponse {
  tenants: MspOnboardingSummaryEntry[];
}

// ── Notification Types ─────────────────────────────────────

// Sentinel string the backend returns in place of stored secret fields
// (e.g. webhook signing_secret). PATCH callers send it back unchanged to
// indicate "preserve the stored value". Mirrors REDACTED_SENTINEL in
// src/shared/db/notification_models.py.
export const REDACTED_SENTINEL = "***";

export type NotificationSeverity = "low" | "medium" | "high" | "critical";

export type NotificationCategory =
  | "auth"
  | "email"
  | "network"
  | "malware"
  | "data_exfil"
  | "persistence"
  | "lateral_movement"
  | "privilege_escalation"
  | "cloud"
  | "endpoint"
  | "other";

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "auth",
  "email",
  "network",
  "malware",
  "data_exfil",
  "persistence",
  "lateral_movement",
  "privilege_escalation",
  "cloud",
  "endpoint",
  "other",
];

export interface NotificationConfig {
  id: string;
  tenant_id: string;
  channel: "email" | "slack" | "webhook";
  config: Record<string, unknown>;
  min_severity: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  category_filter: NotificationCategory[];
}

export interface NotificationConfigsResponse {
  configs: NotificationConfig[];
}

export interface CreateNotificationConfigRequest {
  channel: string;
  config: Record<string, unknown>;
  min_severity?: string;
  category_filter?: NotificationCategory[];
}

export interface UpdateNotificationConfigRequest {
  config?: Record<string, unknown>;
  min_severity?: string;
  enabled?: boolean;
  category_filter?: NotificationCategory[];
}

// ── Notification Policy (#665 P1 detect-but-mute) ──────────
export type NotificationPolicyScope = "global" | "category" | "rule";
export type NotificationPolicyChannel = "email" | "slack" | "sms" | "webhook";

export interface NotificationPolicy {
  id: string;
  tenant_id: string;
  scope: NotificationPolicyScope;
  /** rule_id for scope=rule, category for scope=category, null for global */
  target: string | null;
  /** null = all channels */
  channel: NotificationPolicyChannel | null;
  mode: "mute" | "digest" | "escalate_only" | "min_severity";
  min_severity: string | null;
  /** null = permanent mute */
  muted_until: string | null;
  reason: string | null;
  accepted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationPoliciesResponse {
  policies: NotificationPolicy[];
}

export interface UpsertNotificationPolicyRequest {
  scope: NotificationPolicyScope;
  target?: string | null;
  channel?: NotificationPolicyChannel | null;
  mode?: "mute";
  muted_until?: string | null;
  reason?: string | null;
}

export interface TestNotificationResponse {
  status: "sent" | "failed";
  error?: string;
  channel: string;
  latency_ms: number;
}

// ── Branding Types ─────────────────────────────────────────

export interface BrandingConfig {
  company_name?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  logo_url?: string;
  custom_domain?: string;
}

export interface BrandingResponse {
  branding: BrandingConfig | null;
}

export interface LogoUploadResponse {
  upload_url: string;
  fields: Record<string, string>;
  key: string;
}

// ── Billing Types ──────────────────────────────────────────

export interface BillingSubscription {
  id: string;
  tenant_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  billing_provider?: "stripe" | "lemonsqueezy" | "paystack";
  provider_customer_id?: string | null;
  provider_subscription_id?: string | null;
  provider_updated_at?: string | null;
  plan: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingSubscriptionResponse {
  subscription: BillingSubscription | null;
}

export interface UsageRecord {
  period: string;
  endpoints: number;
  events: number;
  alerts: number;
  storage_bytes: number;
}

export interface BillingUsageResponse {
  usage: UsageRecord[];
}

export interface BillingPortalResponse {
  url: string;
  provider?: "stripe" | "lemonsqueezy" | "paystack";
}

export interface BillingCheckoutAttempt {
  id: string;
  tenant_id: string;
  billing_provider?: "lemonsqueezy" | "paystack";
  nonce: string;
  plan: string;
  checkout_url: string | null;
  status: "pending" | "completed" | "expired";
  expires_at: string;
  created_at: string;
  updated_at: string;
  pricing_currency?: "USD";
  pricing_amount?: number;
  settlement_currency?: "ZAR";
  settlement_amount?: number;
  fx_rate?: string;
  fx_source?: string;
  fx_rate_date?: string;
}

export interface BillingSubscribeResponse {
  provider?: "stripe" | "lemonsqueezy" | "paystack";
  subscription?: BillingSubscription | null;
  checkout_url?: string;
  checkout_attempt?: BillingCheckoutAttempt | null;
  price_breakdown?: BillingFxBreakdown;
}

export interface BillingFxBreakdown {
  pricing_currency: "USD";
  pricing_amount: number;
  settlement_currency: "ZAR";
  settlement_amount: number;
  fx_rate: string;
  fx_source: string;
  fx_rate_date: string;
}

export interface PaystackMandate {
  tenant_id: string;
  card_brand: string | null;
  card_last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  billing_anchor_day: number;
  next_charge_at: string;
  status: "active" | "attention" | "canceled";
  failure_count: number;
}

export interface PaystackChargeAttempt extends BillingFxBreakdown {
  id: string;
  provider_transaction_reference: string;
  plan: "essential" | "growth" | "business";
  status:
    | "reserved"
    | "submitted"
    | "pending"
    | "requires_action"
    | "succeeded"
    | "failed";
  period_start: string;
  period_end: string;
  action_url: string | null;
}

export interface BillingStatusResponse {
  provider: "stripe" | "lemonsqueezy" | "paystack";
  status: string;
  subscription: BillingSubscription | null;
  pending_checkout: BillingCheckoutAttempt | null;
  plan_catalog: BillingPlanPrice[];
  price_tax_label?: "No VAT charged" | null;
  tax_invoice_url?: string | null;
  paystack_mandate?: PaystackMandate | null;
  paystack_charges?: PaystackChargeAttempt[];
}

export interface BillingPlanPrice {
  id: "essential" | "growth" | "business";
  amount: number;
  currency: string;
  interval: "month";
  settlement_currency?: "ZAR";
  variable_settlement?: boolean;
}

export interface BillingProfile {
  tenant_id: string;
  legal_name: string;
  registration_number?: string | null;
  billing_email: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  province?: string | null;
  postal_code: string;
  country_code: string;
  created_at: string;
  updated_at: string;
}

export interface BillingProfileResponse {
  profile: BillingProfile | null;
  vat_status: "not_registered";
}

export interface BillingInvoice {
  id: string;
  billing_provider: "paystack" | "lemonsqueezy";
  provider_transaction_reference: string;
  provider_adjustment_reference: string | null;
  related_document_id: string | null;
  document_kind: "invoice" | "credit_note" | "provider_receipt";
  document_number: string | null;
  status: "draft" | "issued" | "external" | "void";
  currency: string;
  subtotal_amount: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  description: string;
  service_period_start?: string | null;
  service_period_end?: string | null;
  provider_document_url: string | null;
  issued_at: string | null;
  created_at: string;
  pricing_currency?: "USD" | null;
  pricing_amount?: number | null;
  fx_rate?: string | null;
  fx_source?: string | null;
  fx_rate_date?: string | null;
}

export interface BillingInvoicesResponse {
  invoices: BillingInvoice[];
  provider_documents: Record<string, string>;
}

export interface BillingInvoiceDownloadResponse {
  filename: string;
  content_type: "application/pdf";
  content_base64: string;
  sha256: string;
}

/** Realtime usage metric */
export interface UsageMetric {
  count: number;
  bytes: number;
}

/** Per-source event breakdown */
export interface EventSourceBreakdown {
  [sourceType: string]: { count: number; bytes: number };
}

/** Estimated cost breakdown */
export interface EstimatedCost {
  ingestion: number;
  storage: number;
  athena: number;
  total: number;
  currency: string;
}

/**
 * Quota severity (#185.7). `null` means no banner. The backend only ever
 * emits a non-null `quota` object in enforce mode (off/observe → `quota: null`).
 */
export type QuotaSeverity = "warning" | "exceeded" | "cap_reached";

/** Compact banner summary attached to GET /api/v1/me/billing-state. */
export interface QuotaBannerSummary {
  /** Always "enforce" when the enclosing `quota` object is non-null. */
  mode: "enforce";
  banner: {
    show: boolean;
    severity: QuotaSeverity | null;
    /** Driving metric's plan-limits key, or null when hidden. */
    metric: string | null;
    pct: number | null;
  };
}

/** One per-metric usage entry in the full quota block. */
export interface QuotaMetricUsage {
  usage: number;
  /** null = unlimited (Enterprise / contracted). */
  limit: number | null;
  /** usage/limit, only when limit is a positive number AND measured; else null. */
  pct: number | null;
  unit: "gb" | "count";
  /** false for storage_gb until #329 lands real S3-inventory measurement. */
  measured: boolean;
  unlimited: boolean;
}

/** Full per-metric quota block attached to GET /api/v1/billing/usage/realtime. */
export interface QuotaBlock {
  /** Always "enforce" when non-null. */
  mode: "enforce";
  enforcement_enabled: boolean;
  billing_behavior: "metered" | "capped" | "exempt";
  trial_exempt: boolean;
  /** YYYYMM. */
  period: string;
  metrics: {
    ingest_gb_month: QuotaMetricUsage;
    query_gb_month: QuotaMetricUsage;
    storage_gb: QuotaMetricUsage;
    endpoint_count: QuotaMetricUsage;
  };
  banner: QuotaBannerSummary["banner"];
}

/** Response from GET /api/v1/billing/usage/realtime */
export interface BillingUsageRealtimeResponse {
  period: string;
  usage: {
    events_ingested: UsageMetric;
    athena_scanned: UsageMetric;
    api_calls: UsageMetric;
  };
  events_by_source: EventSourceBreakdown;
  estimated_cost: EstimatedCost;
  updated_at: string | null;
  /** #185.7 — full per-metric quota block; null in off/observe + on error. */
  quota: QuotaBlock | null;
}

// ── User Management Types ──────────────────────────────────

export interface TenantUser {
  id: string;
  tenant_id: string;
  email: string;
  name: string;
  role: "owner" | "admin" | "analyst" | "viewer";
  cognito_sub?: string | null;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export interface UsersResponse {
  users: TenantUser[];
}

export interface CreateUserRequest {
  email: string;
  name: string;
  role: string;
}

export interface UpdateUserRequest {
  name?: string;
  role?: string;
}

// ── Investigation Types ────────────────────────────────────

export type SearchFilterValue = string | number | Array<string | number>;

export interface SearchFilter {
  field: string;
  op?: "==" | "!=" | "contains" | "startswith" | "endswith" | "in" | "gt" | "lt" | "gte" | "lte";
  value: SearchFilterValue;
}

export interface SearchAggregate {
  group_by: string[];
  fn: "count" | "count_distinct";
  field?: string;
  limit: number;
  direction: "asc" | "desc";
  bucket: "hour" | "day" | "week" | null;
}

export interface SearchRequest {
  free_text?: string;
  time_start: string;
  time_end: string;
  class_uids?: number[];
  filters?: SearchFilter[];
  aggregate?: SearchAggregate;
  limit?: number;
}

export type SearchStartResponse =
  | { execution_id: string }
  | {
      execution_id: null;
      results: [];
      empty_retention_window: true;
    };

export interface NaturalLanguageSearchRequest {
  query: string;
  time_start: string;
  time_end: string;
  timezone: string;
}

export interface NaturalLanguageTranslation {
  mode: "event_filter" | "aggregate";
  query: string;
  search: SearchRequest;
  interpretation: string[];
}

export type SearchStatus =
  | "QUEUED"
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED"
  | "CANCELLED";

/** True while Athena is still working — poll again rather than render. */
export function isSearchInFlight(status: SearchStatus | undefined): boolean {
  return status === "RUNNING" || status === "QUEUED";
}

/** GET /api/v1/investigation/search/<id> */
export interface SearchResult {
  status: SearchStatus;
  results?: Array<Record<string, unknown>> | null;
  row_count?: number;
  bytes_scanned?: number | null;
  query_execution_id?: string;
  error?: string | null;
}

/** GET /api/v1/investigation/entities/timeline/<id> — distinct payload key so
 * cross-reading the search shape is a compile error, not an empty page. */
export interface EntityTimelineResult {
  status: SearchStatus;
  timeline?: Array<Record<string, unknown>> | null;
  error?: string | null;
}

export type EntityTimelineStartResponse =
  | {
      execution_id: string;
      entity_type: string;
      entity_id: string;
    }
  | {
      execution_id: null;
      timeline: [];
      empty_retention_window: true;
      entity_type: string;
      entity_id: string;
    };

export interface SavedSearch {
  id: string;
  tenant_id: string;
  name: string;
  query_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SavedSearchesResponse {
  saved_searches: SavedSearch[];
  limit: number;
  offset: number;
}

// ── Onboarding Types ──────────────────────────────────────

export interface OnboardingStep {
  name: string;
  url: string | null;
  priority: number;
  category: string;
  status: "pending" | "completed";
  optional?: boolean;
}

export interface OnboardingChecklist {
  tenant_id: string;
  progress: string;
  steps: OnboardingStep[];
}

// ── Agent Key Types ───────────────────────────────────────

// `fleet` keys (#180) are minted only via the dedicated fleet-keys API and
// never appear in the self-service `agent/keys` list, but the value is part of
// the purpose enum the DB stores, so the type carries it for completeness.
export type AgentKeyPurpose = "prod" | "test" | "demo" | "fleet";
export type AgentOSType = "linux" | "windows" | "macos";

export interface AgentKey {
  id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  purpose?: AgentKeyPurpose;
  // Host identifier the wizard captures (or auto-generates per #155). Persisted
  // on the row so the agents-tab UI can disambiguate keys minted under the
  // same `name` (#172). NULL on legacy rows that predate the wizard.
  instance_label?: string | null;
  raw_key?: string;
}

export interface AgentKeysResponse {
  keys: AgentKey[];
}

export interface CreateAgentKeyRequest {
  name: string;
  expires_in_days?: number;
  // Install-flow extras (#155). Pass `os` to also receive an
  // `install_url` in the response. `instance_label` is optional —
  // backend generates a default if omitted.
  purpose?: AgentKeyPurpose;
  os?: AgentOSType;
  instance_label?: string;
}

export interface AgentKeyInstall {
  // The signed token, opaque to the client. Useful only for the
  // dashboard's "view full script" tab which fetches the install URL.
  token: string;
  // One-shot URL the customer pipes through bash/PowerShell. Embeds
  // the token + os in the path. Single-use; expires per `expires_at`.
  install_url: string;
  // ISO-8601 timestamp; clients can warn the user before the URL
  // becomes a 410.
  expires_at: string;
  // Resolved instance_label — the backend default if the wizard
  // submitted an empty value.
  instance_label: string;
}

export interface CreateAgentKeyResponse {
  key: AgentKey;
  // Present only when the request included `os` (#155 wizard flow).
  install?: AgentKeyInstall;
}

// ── Fleet Key Types (#180-C) ──────────────────────────────

// The three RMM flavours the fleet-mint endpoint renders a package for (server
// `FLEET_RMM_TYPES`). `generic` is intentionally excluded — it stays on the
// per-key install commands path.
export type FleetRmmType = "connectwise_automate" | "datto_rmm" | "ninjaone";

// A fleet key as listed by `GET /agent/fleet-keys`. host_count / active_host_count
// are server-computed from agent_deployments joined on this key.
export interface FleetKey {
  id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  host_count: number;
  active_host_count: number;
}

export interface FleetKeysResponse {
  fleet_keys: FleetKey[];
}

export interface CreateFleetKeyRequest {
  name: string;
}

// One rendered RMM deployment package (one (rmm_type, os) combo).
export interface FleetRmmPackage {
  package: string;
  instructions: string;
}

// The one-time mint artifact. raw_key + install_cmds + rmm_packages are returned
// ONCE and never retrievable again — re-mint is the recovery path.
export interface FleetKeyMint {
  id: string;
  name: string;
  key_prefix: string;
  raw_key: string;
  created_at: string;
  install_cmds: Record<AgentOSType, string>;
  rmm_packages: Record<FleetRmmType, Record<AgentOSType, FleetRmmPackage>>;
}

export interface FleetKeyMintResponse {
  fleet_key: FleetKeyMint;
}

// ── Deploy Agent Types ────────────────────────────────────

export interface HealthMetrics {
  disk_total_bytes?: number | null;
  disk_free_bytes?: number | null;
  mem_total_bytes?: number | null;
  mem_available_bytes?: number | null;
  cpu_pct?: number | null;
}

export interface DeployAgent {
  id: string;
  tenant_id: string;
  hostname: string;
  install_id: string | null;
  os_type: "linux" | "windows" | "macos";
  status: "active" | "inactive" | "offline";
  last_heartbeat_at: string | null;
  installed_at: string | null;
  created_at: string;
  updated_at: string;
  // Reported by the agent each heartbeat; surfaced in the fleet-key host
  // expansion (#180-C). May be null on a row that has registered but not yet
  // sent a version.
  agent_version?: string | null;
  health_metrics: HealthMetrics | null;
  health_metrics_at: string | null;
}

export interface DeployAgentsResponse {
  agents: DeployAgent[];
  summary: {
    total: number;
    active: number;
    inactive: number;
    offline: number;
  };
}

// ── Superadmin Types ──────────────────────────────────────

export interface PlatformMetrics {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  total_alerts: number;
  open_alerts: number;
  total_integrations: number;
}

export interface TenantsByPlan {
  plan: string;
  count: number;
}

export interface SuperadminMetricsResponse {
  metrics: PlatformMetrics;
  tenants_by_plan: TenantsByPlan[];
}

export interface SuperadminTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  parent_msp_id: string | null;
  partner_tier: string | null;
  user_count: number;
  created_at: string;
}

export interface SuperadminTenantsResponse {
  tenants: SuperadminTenant[];
  total: number;
  limit: number;
  offset: number;
}

export interface SuperadminUser {
  id: string;
  tenant_id: string;
  email: string;
  name: string;
  role: string;
  is_msp_user: boolean;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  tenant_name: string;
}

export interface SuperadminUsersResponse {
  users: SuperadminUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlatformAuditEntry {
  id: number;
  actor_user_id: string;
  actor_email: string;
  action: string;
  target_tenant_id: string | null;
  target_resource_type: string | null;
  target_resource_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface PlatformAuditResponse {
  entries: PlatformAuditEntry[];
  limit: number;
  offset: number;
}

// ── Compliance Report Types ──────────────────────────────────

export type ComplianceReportStatus = "pending" | "generating" | "completed" | "failed";

export interface ComplianceReport {
  id: string;
  tenant_id: string;
  report_type: string;
  status: ComplianceReportStatus;
  period_start: string;
  period_end: string;
  s3_key: string | null;
  file_size_bytes: number | null;
  include_pii: boolean;
  requested_by: string | null;
  error_message: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComplianceReportsResponse {
  reports: ComplianceReport[];
  limit: number;
  offset: number;
}

export interface ComplianceReportResponse {
  report: ComplianceReport;
  download_url?: string;
}

export interface CreateComplianceReportRequest {
  period_start: string;
  period_end: string;
  include_pii?: boolean;
}

// ── Vulnerability Types ──────────────────────────────────

/** Vulnerability severity levels */
export type VulnSeverity = "critical" | "high" | "medium" | "low" | "unknown";
export type VulnStatus = "open" | "acknowledged" | "resolved" | "false_positive";

/** Vulnerability from GET /api/v1/vulnerabilities */
export interface Vulnerability {
  id: string;
  tenant_id: string;
  asset_id: string;
  hostname: string;
  cve_id: string;
  package_name: string;
  package_version: string;
  fixed_version: string | null;
  severity: VulnSeverity;
  cvss_score: number | null;
  cvss_vector: string | null;
  cve_description: string | null;
  status: VulnStatus;
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

// ── CSPM Types ──────────────────────────────────────────────

export type CspmFindingStatus = "open" | "resolved" | "suppressed";
export type CloudProvider = "aws" | "azure" | "gcp";

export interface CspmFinding {
  id: string;
  tenant_id: string;
  check_id: string;
  cloud_provider: CloudProvider;
  resource_id: string;
  resource_type: string;
  region: string | null;
  severity: AlertSeverity;
  status: CspmFindingStatus;
  title: string;
  description: string | null;
  remediation: string | null;
  details: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  suppressed_by: string | null;
  suppressed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /api/v1/vulnerabilities response */
export interface VulnerabilitiesResponse {
  vulnerabilities: Vulnerability[];
  limit: number;
  offset: number;
}

export interface CspmFindingsResponse {
  findings: CspmFinding[];
  limit: number;
  offset: number;
}

/** GET /api/v1/vulnerabilities/summary response */
export interface VulnerabilitySummaryResponse {
  summary: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    unknown: number;
    total: number;
  };
}

/** Threat intel summary from GET /api/v1/threat-intel/summary */
export interface ThreatIntelSummary {
  total_indicators: number;
  by_type: Record<string, number>;
  by_feed: Record<string, number>;
}

/** Single threat intel indicator from search results */
export interface ThreatIntelIndicator {
  indicator: string;
  indicator_type: "ip" | "domain" | "file_hash" | "url";
  sources: string[];
  categories: string[];
  confidence: number;
  last_seen: string;
}

/** Threat intel search response from GET /api/v1/threat-intel/search */
export interface ThreatIntelSearchResponse {
  results: ThreatIntelIndicator[];
}

/** Single feed status */
export interface ThreatIntelFeedInfo {
  name: string;
  display_name: string;
  types: string[];
  requires_key: boolean;
}

/** Feed list response from GET /api/v1/threat-intel/feeds */
export interface ThreatIntelFeedsResponse {
  feeds: ThreatIntelFeedInfo[];
}

// ── Report Schedule Types ──────────────────────────────────

export type ReportType = "soc2" | "posture" | "msp_portfolio";
export type ReportFrequency = "weekly" | "monthly";

export interface ReportSchedule {
  id: string;
  tenant_id: string;
  report_type: ReportType;
  frequency: ReportFrequency;
  recipients: string[];
  day_of_week: number | null;
  day_of_month: number | null;
  next_run_at: string;
  last_run_at: string | null;
  last_report_id: string | null;
  enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportSchedulesResponse {
  schedules: ReportSchedule[];
}

export interface CreateReportScheduleRequest {
  report_type: ReportType;
  frequency: ReportFrequency;
  recipients: string[];
  day_of_week?: number;
  day_of_month?: number;
}

export interface UpdateReportScheduleRequest {
  enabled?: boolean;
  recipients?: string[];
  frequency?: ReportFrequency;
  day_of_week?: number;
  day_of_month?: number;
}

export interface ReportHistoryResponse {
  reports: ComplianceReport[];
  limit: number;
  offset: number;
}

export interface CspmPostureSummary {
  score: number;
  open: number;
  resolved: number;
  suppressed: number;
  total: number;
  by_severity: SeverityCounts;
  by_provider: Record<string, number>;
}

export interface CspmCheckDefinition {
  check_id: string;
  cloud_provider: CloudProvider;
  title: string;
  description: string;
  severity: AlertSeverity;
  resource_type: string;
  remediation: string;
}

export interface CspmChecksResponse {
  checks: CspmCheckDefinition[];
}

export interface CspmScanResult {
  tenant_id: string;
  started_at: string;
  completed_at: string;
  checks_run: number;
  findings_upserted: number;
  findings_resolved: number;
  errors: Array<{ check_id?: string; integration_id?: string; error: string }>;
}

// ── Microsoft OAuth Types ──────────────────────────────────

/**
 * Entra ID licence state surfaced by `GET /api/v1/oauth/microsoft/status`
 * (#177b). `signin_logs_enabled` drives full-vs-audit-only coverage:
 * `true` = P1/P2 sign-in threat detection on; `false` = confirmed Free,
 * directory-audit-only; `null` = indeterminate (NOT a confirmed Free — the
 * UI must not claim a degrade in that case).
 */
export interface EntraLicenseState {
  license_tier: string | null;
  signin_logs_enabled: boolean | null;
}

export interface MicrosoftOAuthStatus {
  connected: boolean;
  azure_tenant_id?: string;
  connected_at?: string;
  connected_by?: string;
  integrations?: string[];
  /** `null` when there is no entra_id integration for this tenant. */
  entra?: EntraLicenseState | null;
}

export interface MicrosoftOAuthInitiateResponse {
  consent_url: string;
  state: string;
}

export interface MicrosoftOAuthCallbackRequest {
  admin_consent: string;
  tenant: string;
  state: string;
  error?: string;
  error_description?: string;
}

export interface MicrosoftOAuthCallbackResponse {
  status: string;
  azure_tenant_id: string;
  integrations: Array<{
    id: string;
    source_type: string;
    health_status: string;
    migrated: boolean;
  }>;
}

// ── Executive Dashboard Types ──────────────────────────────

export interface RiskFactor {
  name: string;
  weight: number;
  normalized_score: number;
  weighted_contribution: number;
}

export interface TrendMetric {
  name: string;
  current: number;
  previous: number;
  change_pct: number | null;
  direction: "improving" | "stable" | "degrading";
}

export interface InsuranceItem {
  item: string;
  status: "pass" | "fail";
  detail: string;
}

export interface TopRule {
  rule_name: string;
  alert_count: number;
}

export interface AiNarrative {
  executive_summary: string;
  key_highlights: string[];
  recommendations: string[];
  token_usage?: { input_tokens: number; output_tokens: number };
}

export interface ExecutiveSummaryResponse {
  risk_score: number;
  risk_label: "low" | "medium" | "high" | "critical";
  risk_factors: RiskFactor[];
  trends: TrendMetric[];
  severity_distribution: SeverityCounts;
  top_rules: TopRule[];
  insurance_readiness: InsuranceItem[];
  ai_narrative: AiNarrative | null;
  dashboard: DashboardSummary;
}

export interface ExecutiveMetricsResponse {
  period_days: number;
  current_period: {
    alerts: number;
    incidents: number;
    mttr_hours: number | null;
  };
  previous_period: {
    alerts: number;
    incidents: number;
    mttr_hours: number | null;
  };
  severity_distribution: SeverityCounts;
  top_rules: TopRule[];
  alert_trends: TrendDataPoint[];
}

export interface ExecutiveExportResponse {
  export: {
    generated_at: string;
    tenant_name: string;
    period: string;
    risk_score: number;
    risk_label: string;
    risk_factors: Array<{ name: string; score: number; weight: number }>;
    trends: TrendMetric[];
    severity_distribution: SeverityCounts;
    top_rules: TopRule[];
    insurance_readiness: InsuranceItem[];
    open_alerts: SeverityCounts;
    open_incidents: number;
  };
}

// ── Billing / Lifecycle State ─────────────────────────────

/**
 * Lifecycle states as returned by GET /api/v1/me/billing-state.
 *
 * Mirrors `shared.lifecycle.policy.STATE_*` on the backend
 * (`src/shared/lifecycle/policy.py`). The grace state is named
 * `trial_expired_grace` end-to-end — the task-contract shorthand
 * `'grace'` is not the wire value.
 */
export type LifecycleState = "trial" | "paid" | "trial_expired_grace" | "closed";

/**
 * Per-device freshness status — values match the server-side
 * `monitoring.freshness.compute_status` result. Frontend never
 * recomputes thresholds.
 */
export type HostStatus = "active" | "stale" | "dead" | "never_seen";

/** One row in GET /api/v1/me/hosts response. */
export interface HostRow {
  id: string;
  key_prefix: string;
  instance_label: string | null;
  os: "windows" | "macos" | "linux" | "unknown" | null;
  purpose: AgentKeyPurpose;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  status: HostStatus;
  expected_freshness_seconds: number;
}

/** Fleet-wide counts by status; used by the AppShell freshness chip. */
export interface HostsSummary {
  active: number;
  stale: number;
  dead: number;
  never_seen: number;
}

/** Response from GET /api/v1/me/hosts. */
export interface HostsResponse {
  hosts: HostRow[];
  summary: HostsSummary;
}

/** Authoritative identity for the effective tenant backing the app shell. */
export interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

/** Response from GET /api/v1/me/billing-state (see design §11). */
export interface BillingState {
  enforcement_enabled: boolean;
  lifecycle_state: LifecycleState;
  trial_exempt: boolean;
  trial_started_at: string | null;
  trial_expires_at: string | null;
  grace_expires_at: string | null;
  closed_at: string | null;
  cap_used: number | null;
  cap_limit: number | null;
  write_access: "full" | "restricted" | "none";
  days_remaining: number | null;
  grace_days_remaining: number | null;
  /** #185.7 — compact quota summary; null in off/observe + on error. */
  quota: QuotaBannerSummary | null;
}
